/**
 * Track Listing View Component
 * Automatically tracks when a listing is viewed
 */

import { useEffect, useRef } from 'react';
import { getAnalytics } from '../../utils/analytics';
import { clearListingOrigin, readListingOrigin } from '../../utils/listing-click-origin';

interface TrackListingViewProps {
  listing: {
    id: number;
    title?: string;
    price?: number;
    categoryId?: number;
    categoryGroupId?: number;
    tags?: string[];
  };
  /**
   * True when the current viewer owns this listing. Owners can now see the full
   * buyer view of their own listing, so counting their visits would pad its
   * view count. The caller resolves ownership — this component stays pure.
   */
  isOwnListing?: boolean;
  /**
   * False while the session is still resolving. Tracking before that would
   * count every owner's visit, since ownership isn't known yet. Defaults to
   * true so callers that don't care are unaffected.
   */
  sessionReady?: boolean;
}

export function TrackListingView({
  listing,
  isOwnListing = false,
  sessionReady = true,
}: TrackListingViewProps) {
  const listingRef = useRef(listing);
  // Which listing id we've already counted, so the extra render caused by the
  // session resolving doesn't double-count the same view.
  const trackedIdRef = useRef<number | null>(null);

  // Keep the ref in sync with the latest listing; read in the tracking effect below.
  useEffect(() => {
    listingRef.current = listing;
  }, [listing]);

  useEffect(() => {
    if (!sessionReady) return;

    const current = listingRef.current;
    if (!current?.id) return;
    if (trackedIdRef.current === current.id) return;
    if (isOwnListing) return;

    trackedIdRef.current = current.id;

    // Read the click origin HERE, inside the effect, rather than during render.
    // React 19 can start a render and discard it; a destructive read in render
    // would eat the origin permanently on a discarded pass and attribute the
    // view to nothing, intermittently and only under concurrent rendering.
    // Cleared only after the event has actually been handed to the tracker.
    const origin = readListingOrigin(current.id);

    getAnalytics()?.listingView({
      id: current.id,
      title: current.title,
      price: current.price,
      categoryId: current.categoryId,
      categoryGroupId: current.categoryGroupId,
      tags: current.tags,
      resultPosition: origin?.resultPosition,
      totalResults: origin?.totalResults,
      listName: origin?.listName,
      rowIndex: origin?.rowIndex,
      pageType: origin?.pageType,
      searchQuery: origin?.searchQuery,
      activeFilters: origin?.activeFilters,
      randomized: origin?.randomized,
    });

    if (origin) clearListingOrigin();
  }, [listing?.id, sessionReady, isOwnListing]);

  return null; // This component doesn't render anything
}
